import { useId } from 'react'

/** Логотип как в `frontend/src/components/Logo.js` — TOIS.TECH + servicedesk */
export function BrandLogo({ variant = 'full' }: { variant?: 'full' | 'compact' }) {
  const uid = useId().replace(/:/g, '')
  const gid = `brand-lg-${uid}`
  const glow = `brand-glow-${uid}`

  if (variant === 'compact') {
    return (
      <svg
        className="brand-logo brand-logo--compact"
        viewBox="0 0 60 45"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'var(--accent)', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: 'var(--primary)', stopOpacity: 0.8 }} />
          </linearGradient>
          <filter id={glow}>
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g transform="translate(-8, 5) scale(1.2)">
          <path
            d="M 20,8 L 32,2 L 44,8 L 44,22 L 32,28 L 20,22 Z"
            fill="none"
            stroke={`url(#${gid})`}
            strokeWidth="1.5"
            opacity={0.6}
          />
          <path
            d="M 26,12 L 32,8 L 38,12 L 32,20 Z"
            fill={`url(#${gid})`}
            opacity={0.3}
          />
          <line
            x1="32"
            y1="8"
            x2="32"
            y2="20"
            stroke="var(--accent)"
            strokeWidth="1"
            opacity={0.5}
          />
          <line
            x1="26"
            y1="12"
            x2="38"
            y2="12"
            stroke="var(--accent)"
            strokeWidth="1"
            opacity={0.5}
          />
          <circle cx="32" cy="2" r="2" fill="var(--accent)" opacity={0.9} filter={`url(#${glow})`} />
          <circle cx="20" cy="8" r="2" fill="var(--accent)" opacity={0.8} />
          <circle cx="44" cy="8" r="2" fill="var(--accent)" opacity={0.8} />
          <circle cx="44" cy="22" r="2" fill="var(--accent)" opacity={0.8} />
          <circle cx="32" cy="28" r="2" fill="var(--accent)" opacity={0.9} filter={`url(#${glow})`} />
          <circle cx="20" cy="22" r="2" fill="var(--accent)" opacity={0.8} />
          <circle cx="32" cy="15" r="3" fill="var(--primary)" opacity={0.9} filter={`url(#${glow})`} />
          <circle cx="32" cy="15" r="1.5" fill="var(--bg-primary)" />
        </g>
      </svg>
    )
  }

  return (
    <svg
      className="brand-logo brand-logo--full"
      viewBox="0 0 320 140"
      preserveAspectRatio="xMinYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="TOIS.TECH servicedesk"
    >
      <title>TOIS.TECH servicedesk</title>
      <defs>
        <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: 'var(--accent)', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: 'var(--primary)', stopOpacity: 0.8 }} />
        </linearGradient>
        <filter id={glow}>
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g className="brand-logo__icon" transform="translate(12, 30) scale(1.8)">
        <path
          d="M 20,8 L 32,2 L 44,8 L 44,22 L 32,28 L 20,22 Z"
          fill="none"
          stroke={`url(#${gid})`}
          strokeWidth="1.5"
          opacity={0.6}
        />
        <path d="M 26,12 L 32,8 L 38,12 L 32,20 Z" fill={`url(#${gid})`} opacity={0.3} />
        <line
          x1="32"
          y1="8"
          x2="32"
          y2="20"
          stroke="var(--accent)"
          strokeWidth="1"
          opacity={0.5}
        />
        <line
          x1="26"
          y1="12"
          x2="38"
          y2="12"
          stroke="var(--accent)"
          strokeWidth="1"
          opacity={0.5}
        />
        <circle cx="32" cy="2" r="2" fill="var(--accent)" opacity={0.9} filter={`url(#${glow})`} />
        <circle cx="20" cy="8" r="2" fill="var(--accent)" opacity={0.8} />
        <circle cx="44" cy="8" r="2" fill="var(--accent)" opacity={0.8} />
        <circle cx="44" cy="22" r="2" fill="var(--accent)" opacity={0.8} />
        <circle cx="32" cy="28" r="2" fill="var(--accent)" opacity={0.9} filter={`url(#${glow})`} />
        <circle cx="20" cy="22" r="2" fill="var(--accent)" opacity={0.8} />
        <circle cx="32" cy="15" r="3" fill="var(--primary)" opacity={0.9} filter={`url(#${glow})`} />
        <circle cx="32" cy="15" r="1.5" fill="var(--bg-primary)" />
        <path
          d="M 28,15 L 32,11 L 36,15 L 32,19 Z"
          fill="none"
          stroke="var(--accent)"
          strokeWidth="0.5"
          opacity={0.4}
        />
      </g>
      <g className="brand-logo__text">
        <text
          x="115"
          y="55"
          fontFamily="'Inter', 'Segoe UI', system-ui, sans-serif"
          fontSize="34"
          fontWeight="700"
          fill="var(--text-primary)"
          letterSpacing="3"
          textAnchor="start"
        >
          TOIS.TECH
        </text>
        <text
          x="195"
          y="90"
          fontFamily="'Inter', 'Segoe UI', system-ui, sans-serif"
          fontSize="22"
          fontWeight="400"
          fill="var(--text-secondary)"
          letterSpacing="3"
          textAnchor="middle"
        >
          servicedesk
        </text>
        <line
          x1="115"
          y1="65"
          x2="275"
          y2="65"
          stroke="var(--accent)"
          strokeWidth="2.5"
          opacity={0.3}
        />
      </g>
    </svg>
  )
}
